import { collection, query, where, getDocs, updateDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import { getFunctions, httpsCallable } from "firebase/functions";

const MILESTONES = [10, 25, 50];

export function milestonePopupKey(milestone) {
  return `milestone_${milestone}_popup_shown`;
}

export function milestoneEmailKey(milestone) {
  return `milestone_${milestone}_email_sent`;
}

function legacyNotifiedKey(milestone) {
  return `milestone_${milestone}_notified`;
}

function shouldShowPopup(profile, milestone) {
  if (!profile) return true;
  return profile[milestonePopupKey(milestone)] !== true;
}

function shouldSendEmail(profile, milestone) {
  if (!profile) return true;
  if (profile[milestoneEmailKey(milestone)] === true) return false;
  if (profile[legacyNotifiedKey(milestone)] === true) return false;
  return true;
}

async function loadProfile(userId, userProfile) {
  if (userProfile) return userProfile;
  const userQuery = query(collection(db, "users"), where("uid", "==", userId));
  const userSnapshot = await getDocs(userQuery);
  if (userSnapshot.empty) return null;
  return userSnapshot.docs[0].data();
}

async function countLevel1UltraReferrals(userId) {
  const referralsQuery = query(
    collection(db, "referrals"),
    where("referrerId", "==", userId)
  );
  const referralsSnapshot = await getDocs(referralsQuery);
  if (referralsSnapshot.empty) return 0;

  const referredUserIds = referralsSnapshot.docs.map(
    (doc) => doc.data().referredUserId
  );
  if (referredUserIds.length === 0) return 0;

  let ultraCount = 0;
  const batchSize = 10;
  for (let i = 0; i < referredUserIds.length; i += batchSize) {
    const batch = referredUserIds.slice(i, i + batchSize);
    const usersQuery = query(
      collection(db, "users"),
      where("uid", "in", batch)
    );
    const usersSnapshot = await getDocs(usersQuery);
    usersSnapshot.docs.forEach((docSnap) => {
      if (docSnap.data().hasPurchasedPackage === true) ultraCount++;
    });
  }
  return ultraCount;
}

/**
 * Highest milestone tier the user has reached but not yet shown a popup for.
 */
export async function checkMilestonePopupPending(userId, userProfile = null) {
  try {
    const profile = await loadProfile(userId, userProfile);
    const ultraCount = await countLevel1UltraReferrals(userId);
    if (ultraCount < MILESTONES[0]) return null;

    for (let i = MILESTONES.length - 1; i >= 0; i--) {
      const milestone = MILESTONES[i];
      if (ultraCount < milestone) continue;
      if (!shouldShowPopup(profile, milestone)) continue;
      return {
        milestone,
        actualCount: ultraCount,
        needsEmail: shouldSendEmail(profile, milestone),
        popupKey: milestonePopupKey(milestone),
      };
    }
    return null;
  } catch (error) {
    console.error("Error checking milestone popup:", error);
    return null;
  }
}

/**
 * Email not yet sent for a reached milestone (e.g. referrer on order confirm).
 */
export async function checkMilestoneEmailPending(userId, userProfile = null) {
  try {
    const profile = await loadProfile(userId, userProfile);
    const ultraCount = await countLevel1UltraReferrals(userId);
    if (ultraCount < MILESTONES[0]) return null;

    for (let i = MILESTONES.length - 1; i >= 0; i--) {
      const milestone = MILESTONES[i];
      if (ultraCount < milestone) continue;
      if (!shouldSendEmail(profile, milestone)) continue;
      return { milestone, actualCount: ultraCount };
    }
    return null;
  } catch (error) {
    console.error("Error checking milestone email:", error);
    return null;
  }
}

/** @deprecated Use checkMilestonePopupPending or checkMilestoneEmailPending */
export async function checkMilestoneAchievement(userId, userProfile = null) {
  return checkMilestonePopupPending(userId, userProfile);
}

export async function notifyMilestoneAchievement(userData, milestone) {
  try {
    const functions = getFunctions();
    const notifyMilestone = httpsCallable(functions, "notifyMilestoneReward");

    const result = await notifyMilestone({
      userId: userData.uid,
      userEmail: userData.email,
      userName: userData.name || userData.displayName,
      referralId: userData.myReferralId,
      ultraCount: milestone,
    });

    console.log("Milestone notification sent:", result.data);
    return result.data.success;
  } catch (error) {
    console.error("Error notifying milestone achievement:", error);
    return false;
  }
}

export async function markMilestonePopupShown(userId, milestone) {
  try {
    const userQuery = query(
      collection(db, "users"),
      where("uid", "==", userId)
    );
    const userSnapshot = await getDocs(userQuery);
    if (userSnapshot.empty) return false;

    await updateDoc(userSnapshot.docs[0].ref, {
      [milestonePopupKey(milestone)]: true,
      [`milestone_${milestone}_date`]: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    return true;
  } catch (error) {
    console.error("Error marking milestone popup shown:", error);
    return false;
  }
}

export async function markMilestoneEmailSent(userId, milestone) {
  try {
    const userQuery = query(
      collection(db, "users"),
      where("uid", "==", userId)
    );
    const userSnapshot = await getDocs(userQuery);
    if (userSnapshot.empty) return false;

    await updateDoc(userSnapshot.docs[0].ref, {
      [milestoneEmailKey(milestone)]: true,
      [legacyNotifiedKey(milestone)]: true,
      updatedAt: new Date().toISOString(),
    });
    return true;
  } catch (error) {
    console.error("Error marking milestone email sent:", error);
    return false;
  }
}

/** @deprecated Use markMilestonePopupShown / markMilestoneEmailSent */
export async function markMilestoneNotified(userId, milestone, notifiedKey) {
  await markMilestoneEmailSent(userId, milestone);
  await markMilestonePopupShown(userId, milestone);
  return true;
}

export function getMilestoneDetails(milestone) {
  const milestones = {
    10: {
      title: "10 Ultra Customers Milestone!",
      message:
        "Congratulations! You have achieved 10 Ultra customers at Level 1. Contact admin to receive Meta Whale merchandise such as a T-shirt, cap, or kurta for ladies.",
      rewards: [
        "Meta Whale T-shirt",
        "Meta Whale Cap",
        "Meta Whale Kurta (for ladies)",
      ],
    },
    25: {
      title: "25 Ultra Customers Milestone!",
      message:
        "Amazing! You have achieved 25 Ultra customers at Level 1. Contact admin to receive rewards such as a leather bag, diary, and educational materials.",
      rewards: ["Premium Leather Bag", "Branded Diary", "Educational Materials"],
    },
    50: {
      title: "50 Ultra Customers Milestone!",
      message:
        "Incredible! You have achieved 50 Ultra customers at Level 1. Contact admin to receive a premium watch.",
      rewards: ["Premium Watch"],
    },
  };

  return milestones[milestone] || null;
}
